import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Send,
  ExternalLink,
  Users,
  CheckCircle,
  MessageCircle,
  Play,
  MoreVertical,
  Zap,
} from 'lucide-react'
import {
  campaignsApi,
  companiesApi,
  type CampaignCompany,
  type OutreachStatus,
} from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { OutreachStatusBadge } from '@/components/outreach-status-badge'
import { PipelineStageBadge, type PipelineStage } from '@/components/pipeline-stage-badge'
import { AddCompaniesToCampaignDialog } from '@/components/add-companies-to-campaign-dialog'

const STATUS_FILTERS: { value: OutreachStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'ready', label: 'Ready' },
  { value: 'sent', label: 'Sent' },
  { value: 'replied', label: 'Replied' },
  { value: 'bounced', label: 'Bounced' },
]

export default function CampaignDetailPage() {
  const { id: campaignId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<OutreachStatus | 'all'>(
    'all'
  )
  const [search, setSearch] = useState('')
  const [page] = useState(1)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => campaignsApi.get(campaignId!),
    enabled: !!campaignId,
  })

  const { data: companiesData, isLoading: companiesLoading } = useQuery({
    queryKey: [
      'campaign-companies',
      campaignId,
      page,
      statusFilter,
      search,
    ],
    queryFn: () =>
      campaignsApi.listCompanies(campaignId!, {
        page,
        per_page: 50,
        outreach_status:
          statusFilter === 'all' ? undefined : statusFilter,
        search: search || undefined,
      }),
    enabled: !!campaignId,
  })

  const launchMutation = useMutation({
    mutationFn: () => campaignsApi.launch(campaignId!, { send_email: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
      queryClient.invalidateQueries({
        queryKey: ['campaign-companies', campaignId],
      })
    },
  })

  const processPendingMutation = useMutation({
    mutationFn: async () => {
      const allCompanyIds: string[] = []
      let currentPage = 1
      let hasMore = true

      while (hasMore) {
        const response = await campaignsApi.listCompanies(campaignId!, {
          page: currentPage,
          per_page: 100,
          outreach_status: 'pending',
        })
        allCompanyIds.push(...response.items.map((c) => c.raw_company_id))
        hasMore = response.items.length === 100
        currentPage++
      }

      if (allCompanyIds.length === 0) {
        throw new Error('No pending companies to process')
      }
      return companiesApi.runPipeline(allCompanyIds, { sendEmail: false })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
      queryClient.invalidateQueries({
        queryKey: ['campaign-companies', campaignId],
      })
    },
  })

  const removeMutation = useMutation({
    mutationFn: (ids: string[]) =>
      campaignsApi.removeCompanies(campaignId!, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] })
      queryClient.invalidateQueries({
        queryKey: ['campaign-companies', campaignId],
      })
      setSelectedIds(new Set())
    },
  })

  const companies = companiesData?.items ?? []
  const allSelected =
    companies.length > 0 &&
    companies.every((c) => selectedIds.has(c.raw_company_id))

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(companies.map((c) => c.raw_company_id)))
    }
  }

  const toggleCompany = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  const handleRemoveSelected = () => {
    if (selectedIds.size > 0) {
      removeMutation.mutate(Array.from(selectedIds))
    }
  }

  if (campaignLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Campaign not found</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/app/campaigns')}
        >
          Back to Campaigns
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/app/campaigns')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{campaign.name}</h1>
          {campaign.description && (
            <p className="text-muted-foreground">{campaign.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {campaign.stats.pending > 0 && (
            <Button
              variant="outline"
              onClick={() => processPendingMutation.mutate()}
              disabled={processPendingMutation.isPending}
            >
              {processPendingMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              Process Pending ({campaign.stats.pending})
            </Button>
          )}
          <Button
            onClick={() => launchMutation.mutate()}
            disabled={
              launchMutation.isPending || campaign.stats.ready === 0
            }
          >
            {launchMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Launch Outreach
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Companies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              <span className="text-2xl font-semibold">
                {campaign.stats.total_companies}
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Outreach Ready
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-semibold">
                {campaign.stats.ready}
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-semibold">
                {campaign.stats.sent}
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Replied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-purple-500" />
              <span className="text-2xl font-semibold">
                {campaign.stats.replied}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Companies</CardTitle>
            <div className="flex items-center gap-2">
              {selectedIds.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveSelected}
                  disabled={removeMutation.isPending}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove ({selectedIds.size})
                </Button>
              )}
              <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Companies
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Input
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Tabs
              value={statusFilter}
              onValueChange={(v) =>
                setStatusFilter(v as OutreachStatus | 'all')
              }
            >
              <TabsList>
                {STATUS_FILTERS.map((f) => (
                  <TabsTrigger key={f.value} value={f.value}>
                    {f.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {companiesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : companies.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No companies found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Pipeline stage</TableHead>
                  <TableHead>Outreach status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(company.raw_company_id)}
                        onCheckedChange={() =>
                          toggleCompany(company.raw_company_id)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/app/companies/${company.raw_company_id}`}
                        className="font-medium hover:underline"
                      >
                        {company.company_name || 'Unknown'}
                      </Link>
                      {company.website && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <a
                            href={
                              company.website.startsWith('http')
                                ? company.website
                                : `https://${company.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {company.website}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {company.contact_name && (
                        <div className="font-medium text-sm">
                          {company.contact_name}
                        </div>
                      )}
                      {company.contact_email && (
                        <div className="text-xs text-muted-foreground">
                          {company.contact_email}
                        </div>
                      )}
                      {!company.contact_name && !company.contact_email && (
                        <span className="text-muted-foreground text-sm">
                          No contact
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {company.pipeline_stage ? (
                        <PipelineStageBadge
                          stage={company.pipeline_stage as PipelineStage}
                          size="sm"
                        />
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <OutreachStatusBadge
                        status={company.outreach_status}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link
                              to={`/app/companies/${company.raw_company_id}`}
                            >
                              View Company
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() =>
                              removeMutation.mutate([company.raw_company_id])
                            }
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AddCompaniesToCampaignDialog
        campaignId={campaignId!}
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
      />
    </div>
  )
}
